import React, { useEffect, useRef } from 'react';
import AvItemWithLogoGroup, { IconSize } from '@components/AvItemWithLogoGroup';
import { IconVariant } from '@components/ItemWithLogo';
import { AvTooltip, ColorItem } from '@fabric-ui/components';
import { cellContentStyle, dotStyle, flex, getCssVarColorWithOpacity } from '@fabric-ui/theme';
import { emptyArray, emptyObject } from '@fabric-ui/utils';
import { Box, BoxProps, capitalize, Divider, MenuItem, Paper } from '@mui/material';
import { format, isValid } from 'date-fns';
import { TooltipProps } from 'recharts';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { isPercentageMetric } from '../../utils/dashboardDataUtils';
import { formatTooltipNumberValue } from '../../views/CustomDashboards/components/Widgets/tooltipNumberFormatter';
import { NO_VALUE } from '../../views/CustomDashboards/constants';
import { ChartClickInteraction } from './types';

type Props<TValue extends ValueType, TName extends NameType> = {
  isDate?: boolean;
  itemSorter?: (payload: any) => any;
  metricKey?: string;
  titleFormatter?: (title: any, payload?: TooltipProps<TValue, TName>['payload']) => string | React.JSX.Element;
  metricFormatter?: (metric: any, payload?: TooltipProps<TValue, TName>['payload']) => string;
  showContentLegend?: boolean;
  showTitleLegend?: boolean;
  showTitle?: boolean;
  tooltipFields?: string[];
  metricsToHide?: string[];
  dateFormat?: string;
  series100Percent?: any[];
  clickInteractions?: ChartClickInteraction[];
  areClickOptionsVisible?: boolean;
  setAreClickOptionsVisible?: (v) => void;
  sx?: object;
  widgetData?: Array<Record<string, unknown>>;
  maxTitleWidth?: number;
  showDivider?: boolean;
  onInteractionsClick?: BoxProps['onClick'];
} & Pick<TooltipProps<TValue, TName>, 'payload'> &
  Omit<BoxProps, 'sx'>;

const CustomTooltip = <TValue extends ValueType, TName extends NameType>({
  payload,
  isDate = false,
  itemSorter,
  metricKey,
  titleFormatter = d => d,
  metricFormatter: metricFormatterProp,
  showContentLegend = true,
  showTitleLegend,
  showTitle = true,
  tooltipFields,
  metricsToHide = emptyArray,
  dateFormat,
  series100Percent = emptyArray,
  clickInteractions,
  areClickOptionsVisible,
  setAreClickOptionsVisible,
  sx = emptyObject,
  widgetData,
  maxTitleWidth = 300,
  showDivider = true,
  onInteractionsClick,
  ...props
}: Props<TValue, TName>) => {
  const tooltipRef = useRef<any>(null);
  const handleOutsideClick = event => {
    if (setAreClickOptionsVisible && tooltipRef.current && !tooltipRef.current.contains(event.target)) {
      setAreClickOptionsVisible(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  if (!payload || payload.length === 0) {
    return null;
  }

  const availableCellClickInteractions = clickInteractions
    ? clickInteractions.filter(v => v.isEnabledForCell({ dataKey: payload[0].dataKey, ...payload[0]?.payload }))
    : clickInteractions;
  const metricFormatter = metricFormatterProp || (d => d);

  const renderContent = series => {
    const item = series.payload.payload;
    if (item) {
      const content = tooltipFields
        ? Object.keys(item).reduce((acc, key) => {
            if (tooltipFields.includes(key)) {
              acc[capitalize(key)] = item[key];
            }
            return acc;
          }, {})
        : null;
      if (content) {
        return Object.keys(content).map(key => {
          const val = content[key];
          const hasCustomMetricFormatter = !!metricFormatterProp;
          const formattedVal = hasCustomMetricFormatter ? metricFormatterProp(val, payload) : undefined;
          const displayVal = hasCustomMetricFormatter ? (formattedVal ?? formatTooltipNumberValue(val)) : formatTooltipNumberValue(val);
          return (
            <Box key={key} sx={{ ...flex.justifyBetween, gap: 3 }}>
              <Box sx={flex.itemsCenter}>
                <Box sx={{ color: 'var(--color-content-immutable-white)', ...flex.itemsCenter, gap: 1 }}>
                  {val.fill && (
                    <Box
                      sx={{
                        ...dotStyle(val.fill),
                        border: '1px solid var(--color-border-base-tertiary)',
                      }}
                    />
                  )}
                  {key}
                </Box>
              </Box>
              <Box sx={{ fontWeight: 600, ...flex.row }}>
                {typeof val !== 'object' ? (
                  displayVal
                ) : (
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 50px', gap: 0.5 }}>
                    {Object.keys(val)
                      .filter(key => key !== 'fill')
                      .map(key => (
                        <Box sx={{ ...flex.justifyEndCenter }} key={`${key}_${val}`}>
                          {val[key]}
                        </Box>
                      ))}
                  </Box>
                )}
              </Box>
            </Box>
          );
        });
      }
    }

    const isPercentage = isPercentageMetric(series.dataKey);
    const metric = metricFormatter(metricKey ? series.payload[metricKey] : series.name, payload);
    const val = series.payload[series.dataKey];
    if (metricsToHide.includes(metric)) {
      return null;
    }
    return (
      <Box key={metric} sx={{ ...flex.justifyBetween, gap: 1 }}>
        <Box sx={{ ...flex.itemsCenter }}>
          {showContentLegend &&
            (Array.isArray(series.color || series.payload.color) ? (
              <AvTooltip variant="message" color="white" hasWidthLimit={false} title="s">
                <div>
                  <AvItemWithLogoGroup
                    items={[...new Set<string>(series.color || series.payload.color)]}
                    variant={IconVariant.color}
                    CustomItemRender={ColorItem}
                    size={IconSize.collapsedXXSmall}
                    limit={3}
                  />
                </div>
              </AvTooltip>
            ) : (
              <Box
                sx={{
                  ...dotStyle(
                    metricKey
                      ? series.payload.fill
                      : series.color === 'transparent'
                        ? 'var(--color-surface-base-tertiary)'
                        : series.color || series.payload.color
                  ),
                  mr: 2,
                  border: '1px solid var(--color-border-base-tertiary)',
                }}
              />
            ))}
          <div>{capitalize(metric ? String(metric) : '')}</div>
        </Box>
        <Box sx={{ fontWeight: 600 }}>
          {series100Percent.some(({ dataKey, is100Percent }) => dataKey === metric && is100Percent)
            ? `${val}%`
            : isPercentage
              ? `${(+val || 0).toFixed(2)}%`
              : formatTooltipNumberValue(val)}
        </Box>
      </Box>
    );
  };
  const title = titleFormatter(metricKey || payload[0]?.payload.name, payload);
  const content = (itemSorter ? itemSorter(payload) : payload).map(series => renderContent(series));
  const hasContent = content[0].length > 0 || content.length > 0;

  return (
    <Box
      ref={tooltipRef}
      sx={{
        ...flex.col,
        gap: 0.5,
        background: 'var(--color-surface-immutable-darkGrey)',
        borderRadius: 2.5,
        color: 'var(--color-content-immutable-white)',
        fontSize: 12,
        pb: availableCellClickInteractions?.length ? 0 : 1,
        ...sx,
      }}
      {...props}>
      {showTitle && (
        <>
          <Box sx={{ ...flex.itemsCenter, fontWeight: 600, marginBottom: '2px', px: 1, pt: 1 }}>
            {showTitleLegend && (
              <Box
                sx={{
                  ...dotStyle(metricKey ? payload[0]?.payload.fill : payload[0]?.color || payload[0]?.payload.color),
                  border: '1px solid var(--color-border-base-tertiary)',
                }}
              />
            )}
            <Box
              sx={{
                width: '100%',
                maxWidth: maxTitleWidth,
                ...cellContentStyle,
                WebkitLineClamp: 2,
                px: 1,
              }}>
              {isDate && isValid(new Date(payload[0]?.payload?.date))
                ? payload[0]?.payload?.date === null
                  ? NO_VALUE
                  : format(new Date(payload[0]?.payload?.date), dateFormat || 'MMMM yyyy')
                : title === null
                  ? NO_VALUE
                  : title}
            </Box>
          </Box>
          {/* @ts-ignore */}
          {hasContent && showDivider && <Divider variant="center" sx={{ borderColor: 'var(--color-content-immutable-grey)' }} />}
        </>
      )}
      {hasContent && showDivider && <Box sx={{ px: 1, pt: showTitle ? 0 : 1 }}>{content}</Box>}
      {availableCellClickInteractions?.length ? (
        <>
          {/* @ts-ignore */}
          <Divider variant="center" sx={{ borderColor: 'var(--color-content-immutable-grey)' }} />
          {areClickOptionsVisible ? (
            <Paper
              sx={{
                ...flex.col,
                pointerEvents: 'all',
                mt: -0.5,
                borderRadius: 0,
                borderBottomRightRadius: '10px',
                borderBottomLeftRadius: '10px',
                p: 1,
              }}>
              {availableCellClickInteractions.map(({ title, onClick, icon }) => (
                <MenuItem
                  key={title}
                  onClick={() =>
                    onClick({
                      d: payload[0]?.payload,
                      dataKey: payload[0].dataKey,
                      widgetData,
                      originalValue: payload[0]?.payload?.originalValue,
                      headCellType: payload[0]?.payload?.headCellType,
                    })
                  }
                  sx={{ background: 'white', fontSize: '12px', px: 1 }}>
                  {icon}
                  {title}
                </MenuItem>
              ))}
            </Paper>
          ) : (
            <Box
              sx={{
                fontSize: '12px',
                color: getCssVarColorWithOpacity('var(--color-content-immutable-white)', 0.7),
                pb: 0.5,
                px: 1,
                cursor: 'pointer',
              }}
              onClick={onInteractionsClick}>
              Click{' '}
              {availableCellClickInteractions.length === 1 ? (
                <span>
                  to <span style={{ fontWeight: 500 }}>{availableCellClickInteractions[0].title.toLowerCase()}</span>
                </span>
              ) : (
                'for interactions'
              )}
            </Box>
          )}
        </>
      ) : null}
    </Box>
  );
};

export default CustomTooltip;
